import React from "react";
import { MediaType } from "shared/utils/enums";
import { WebCamStateType } from "./type";
import { webCamInitialState } from "./utils";

export default function useWebCamCapture(setCapturedImage: Function, handleBroswerSupport: Function) {
  const [state, setState] = React.useState<WebCamStateType>(webCamInitialState);
  const webcamRef = React.useRef<any>(null);

  const videoConstraints = React.useMemo(
    () => ({
      facingMode: "user",
      deviceId: state.selectedDevice?.deviceId || ""
    }),
    [state.selectedDevice?.deviceId]
  );

  React.useEffect(() => {
    let isMounted = true;
    if (isMounted && !navigator.mediaDevices.enumerateDevices) {
      handleBroswerSupport();
    } else {
      navigator.mediaDevices
        .enumerateDevices()
        .then(mediaDevices => {
          const vDevices = mediaDevices.filter(({ kind }) => kind === MediaType.VIDEO);

          isMounted &&
            vDevices.length &&
            setState({
              ...state,
              selectedDevice: vDevices[0],
              devices: vDevices
            });
        })
        .catch(() => {
          return;
        });
    }
    return () => {
      isMounted = false;
    };
  }, [handleBroswerSupport, state]);

  const optionsForDeviceSelect = React.useMemo(() => {
    return state.devices?.map((device: MediaDeviceInfo) => ({
      label: device.label,
      value: device.deviceId
    }));
  }, [state.devices]);

  const capture = React.useCallback(() => {
    const imageSrc = webcamRef.current && webcamRef.current.getScreenshot();
    if (!imageSrc) {
      return;
    }
    setCapturedImage(imageSrc);
  }, [setCapturedImage]);

  const handleDeviceSelect = React.useCallback(
    (device: any) => {
      const selectedDevice = state.devices?.find((d: MediaDeviceInfo) => d.deviceId === device.value);
      setState(prevState => ({
        ...prevState,
        selectedDevice: selectedDevice
      }));
    },
    [state.devices]
  );

  return {
    videoConstraints,
    capture,
    handleDeviceSelect,
    state,
    setState,
    webcamRef,
    optionsForDeviceSelect
  };
}
